/**
 * notifications.tsx — Harmonia v6
 * Design : identité Harmonia RÉELLE, alignée sur actu.tsx
 *   Violet #8A2BE2 → Indigo #4B0082 (dégradé header, comme actu.tsx)
 *   Or #FFD700 (accents, badges, non-lu)
 *   Fond clair #F0F2F5 (identique à actu.tsx)
 * Onglets : Notifications · Info 24h · Trophées
 * Responsive Web + Mobile (Expo React Native)
 *
 * ✅ CACHE-FIRST STRICT (aligné sur actu.tsx) : si un cache existe au montage
 *    (notifs et/ou trophées), aucun fetch réseau automatique n'est déclenché.
 *    Le refresh ne se fait plus que sur :
 *      - pull-to-refresh (onRefresh)
 *      - changement vers l'onglet "Info 24h" tant qu'il n'a jamais été chargé
 *    Le cache reste affiché indéfiniment tant qu'aucun de ces événements ne
 *    se produit, et n'est remplacé qu'après un fetch réseau réussi.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  RefreshControl, Platform, ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';

let Haptics: any = null;
if (Platform.OS !== 'web') Haptics = require('expo-haptics');

const BACKEND = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';

// ── Clés cache AsyncStorage ──────────────────────────────────────────────────
const CACHE_NOTIFS_KEY   = 'harmonia_cache_notifs_v1';
const CACHE_TROPHIES_KEY = 'harmonia_cache_trophies_v1';
const CACHE_24H_KEY      = 'harmonia_cache_notifs24h_v1';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
interface Notif {
  id: string;
  type: string;
  title: string;
  body: string;
  is_read: boolean;
  reference_table: string | null;
  reference_id: string | null;
  total_count: number;
  last_activity_at: string;
  created_at: string;
}
interface Trophy {
  id: string;
  title: string;
  description: string | null;
  reason: string | null;
  awarded_at: string;
  awarder: { nom: string; prenom: string } | null;
}
type Tab = 'notifs' | 'last24h' | 'trophees';

// ─────────────────────────────────────────────────────────────────────────────
// PALETTE HARMONIA — identique à actu.tsx (référence réelle du projet)
// ─────────────────────────────────────────────────────────────────────────────
const H = {
  // Violet / Indigo (dégradé header, identique à actu.tsx)
  violet:      '#8A2BE2',
  indigo:      '#4B0082',
  violetPale:  '#F0E6FF',
  violetLine:  '#E0D0FF',
  violetMuted: '#B39DDB',
  violetSoft:  '#2D0072',

  // Or (identique à actu.tsx)
  gold:   '#FFD700',
  goldBg: '#FFF8E1',

  // Rose/magenta (bouton créer, identique à actu.tsx)
  pink: '#FF0080',

  // Neutres — fond clair, identique à actu.tsx
  bg:      '#F0F2F5',
  surface: '#FFFFFF',
  border:  '#F0F0F0',
  text:    '#1A1A1A',
  soft:    '#333333',
  muted:   '#999999',
  faint:   '#C4B5FD',

  // Statuts (identiques à actu.tsx)
  green: '#10B981',
  red:   '#EF4444',
  blue:  '#3B82F6',
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITAIRES — traduits + locale dynamique
// ─────────────────────────────────────────────────────────────────────────────
const LOCALE_MAP: Record<string, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
};

function fmtRelative(iso: string, t: (key: string, opts?: any) => string, lang: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m    = Math.floor(diff / 60000);
  if (m < 1)  return t('notifications.time.now');
  if (m < 60) return t('notifications.time.minutes', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('notifications.time.hours', { count: h });
  const d = Math.floor(h / 24);
  if (d < 7)  return t('notifications.time.days', { count: d });
  const locale = LOCALE_MAP[lang] || 'fr-FR';
  return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}

function fmtDate(iso: string, lang: string): string {
  const locale = LOCALE_MAP[lang] || 'fr-FR';
  return new Date(iso).toLocaleDateString(locale, {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function haptic() {
  if (Platform.OS !== 'web' && Haptics)
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
}

// ─────────────────────────────────────────────────────────────────────────────
// THÈME PAR TYPE DE NOTIFICATION — traduit
// ─────────────────────────────────────────────────────────────────────────────
function notifTheme(type: string, t: (key: string) => string) {
  switch (type) {
    case 'friend_request':
      return { icon: 'person-add',      color: H.blue,   bg: '#EFF6FF', label: t('notifications.types.friendRequest') };
    case 'friend_accepted':
      return { icon: 'people',          color: H.green,  bg: '#ECFDF5', label: t('notifications.types.friendAccepted') };
    case 'like':
      return { icon: 'heart',           color: '#EC4899',bg: '#FDF2F8', label: t('notifications.types.like') };
    case 'comment':
      return { icon: 'chatbubble',      color: H.blue,   bg: '#EFF6FF', label: t('notifications.types.comment') };
    case 'report':
      return { icon: 'flag',            color: '#EA580C',bg: '#FFF7ED', label: t('notifications.types.report') };
    case 'post_status':
      return { icon: 'shield-checkmark',color: H.violet, bg: H.violetPale, label: t('notifications.types.postStatus') };
    default:
      return { icon: 'notifications',   color: '#B8860B',bg: H.goldBg,  label: t('notifications.types.default') };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'fr';

  const [tab,          setTab]          = useState<Tab>('notifs');
  const [notifs,       setNotifs]       = useState<Notif[]>([]);
  const [notifs24h,    setNotifs24h]    = useState<Notif[]>([]);
  const [trophies,     setTrophies]     = useState<Trophy[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [loading24h,   setLoading24h]   = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [hasMore,      setHasMore]      = useState(false);
  const [nextOffset,   setNextOffset]   = useState(0);
  const [loaded24h,    setLoaded24h]    = useState(false);
  const [error,        setError]        = useState('');

  // Cache-first strict : si un cache existait déjà au montage (notifs et/ou
  // trophées), on ne relance pas de fetch réseau automatique dans loadAll().
  // Le refresh ne se déclenche plus que sur pull-to-refresh ou changement
  // d'onglet vers "Info 24h" tant que ce dernier n'a jamais été chargé.
  const hasCacheRef = useRef(false);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const getAuth = async (): Promise<{ user_id: string; access_token: string } | null> => {
    try {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (!raw) return null;
      let s = JSON.parse(raw);
      if (!s?.access_token || !s?.user?.id) return null;
      const expiresAt = s.expires_at ?? 0;
      if (Math.floor(Date.now() / 1000) > expiresAt - 60 && s.refresh_token) {
        try {
          const r = await fetch(`${BACKEND}/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: s.refresh_token }),
          });
          if (r.ok) {
            const fresh = await r.json();
            s = { ...s, ...fresh };
            await AsyncStorage.setItem('harmonia_session', JSON.stringify(s));
          }
        } catch {}
      }
      return { user_id: s.user.id, access_token: s.access_token };
    } catch { return null; }
  };

  // ── API ───────────────────────────────────────────────────────────────────
  const api = useCallback(async (body: Record<string, any>) => {
    const auth = await getAuth();
    if (!auth) throw new Error(t('notifications.error.notConnected'));
    const res = await fetch(`${BACKEND}/notifications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...auth, ...body }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || t('notifications.error.generic', { status: res.status }));
    return data;
  }, [t]);

  // ── Cache — lecture/écriture AsyncStorage ─────────────────────────────────
  // Renvoie true si au moins une donnée exploitable (notifs ou trophées) a
  // été trouvée en cache — utilisé pour sauter le fetch réseau automatique
  // au montage (cache-first strict).
  const loadCache = useCallback(async (): Promise<boolean> => {
    let hasCache = false;
    try {
      const [rawNotifs, rawTrophies] = await Promise.all([
        AsyncStorage.getItem(CACHE_NOTIFS_KEY),
        AsyncStorage.getItem(CACHE_TROPHIES_KEY),
      ]);
      if (rawNotifs) {
        const cached: Notif[] = JSON.parse(rawNotifs);
        if (cached.length > 0) { setNotifs(cached); hasCache = true; }
      }
      if (rawTrophies) {
        const cached: Trophy[] = JSON.parse(rawTrophies);
        if (cached.length > 0) { setTrophies(cached); hasCache = true; }
      }
    } catch {
      // Cache corrompu → on ignore silencieusement
    }
    return hasCache;
  }, []);

  const saveCache = useCallback(async (newNotifs: Notif[], newTrophies: Trophy[]) => {
    try {
      await Promise.all([
        AsyncStorage.setItem(CACHE_NOTIFS_KEY,   JSON.stringify(newNotifs)),
        AsyncStorage.setItem(CACHE_TROPHIES_KEY, JSON.stringify(newTrophies)),
      ]);
    } catch {
      // Écriture cache échouée → on ignore silencieusement
    }
  }, []);

  const loadCache24h = useCallback(async (): Promise<boolean> => {
    try {
      const raw = await AsyncStorage.getItem(CACHE_24H_KEY);
      if (raw) {
        const cached: Notif[] = JSON.parse(raw);
        if (cached.length > 0) { setNotifs24h(cached); setLoaded24h(true); return true; }
      }
    } catch {
      // Cache corrompu → on ignore silencieusement
    }
    return false;
  }, []);

  const saveCache24h = useCallback(async (newNotifs24h: Notif[]) => {
    try {
      await AsyncStorage.setItem(CACHE_24H_KEY, JSON.stringify(newNotifs24h));
    } catch {
      // Écriture cache échouée → on ignore silencieusement
    }
  }, []);

  // ── Chargement initial (réseau) ───────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setError('');
    try {
      const [dn, dt] = await Promise.all([
        api({ action: 'listNotifications' }),
        api({ action: 'listTrophies' }),
      ]);
      const freshNotifs   = dn.notifications ?? [];
      const freshTrophies = dt.trophies ?? [];
      setNotifs(freshNotifs);
      setHasMore(dn.has_more ?? false);
      setNextOffset(dn.next_offset ?? 20);
      setTrophies(freshTrophies);
      setLoaded24h(false);
      saveCache(freshNotifs, freshTrophies);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [api, saveCache]);

  // ── Chargement 24h (lazy — cache-first, réseau seulement si pas de cache) ─
  const load24h = useCallback(async () => {
    if (loaded24h) return;
    // Cache-first strict : si le cache 24h existe, on l'affiche et on
    // s'arrête là — pas de fetch réseau automatique.
    const foundInCache = await loadCache24h();
    if (foundInCache) return;
    setLoading24h(true);
    setError('');
    try {
      const d = await api({ action: 'listLast24h' });
      const fresh = d.notifications ?? [];
      setNotifs24h(fresh);
      setLoaded24h(true);
      saveCache24h(fresh);
    } catch (e: any) { setError(e.message); }
    finally { setLoading24h(false); }
  }, [api, loaded24h, loadCache24h, saveCache24h]);

  // ── Voir plus ─────────────────────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const d = await api({ action: 'listMore', offset: nextOffset });
      setNotifs(prev => [...prev, ...(d.notifications ?? [])]);
      setHasMore(d.has_more ?? false);
      setNextOffset(d.next_offset ?? nextOffset + 20);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingMore(false); }
  }, [api, loadingMore, hasMore, nextOffset]);

  useEffect(() => {
    loadCache().then(found => {
      hasCacheRef.current = found;
      if (found) {
        // Cache trouvé → on affiche tel quel, pas de fetch réseau automatique
        setLoading(false);
      } else {
        loadAll();
      }
    });
  }, []);

  // ── Changement d'onglet ───────────────────────────────────────────────────
  const handleTabChange = (tKey: Tab) => {
    haptic();
    setTab(tKey);
    if (tKey === 'last24h') load24h();
  };

  const onRefresh = () => {
    haptic();
    setRefreshing(true);
    setLoaded24h(false);
    loadAll();
  };

  // ── Marquer lu ────────────────────────────────────────────────────────────
  const markRead = async (id: string, source: 'notifs' | 'last24h') => {
    haptic();
    if (source === 'notifs')
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    else
      setNotifs24h(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try { await api({ action: 'markAsRead', notification_id: id }); } catch {}
  };

  const markAllRead = async () => {
    haptic();
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    setNotifs24h(prev => prev.map(n => ({ ...n, is_read: true })));
    try { await api({ action: 'markAllAsRead' }); } catch {}
  };

  const unreadCount    = notifs.filter(n => !n.is_read).length;
  const unread24hCount = notifs24h.filter(n => !n.is_read).length;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDU
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <View style={S.root}>

      {/* ══ HEADER — dégradé violet → indigo, identique à actu.tsx ═══════ */}
      <LinearGradient colors={[H.violet, H.indigo]} style={S.header}>
        {/* Titre + retour */}
        <View style={S.headerRow}>
          <TouchableOpacity style={S.backBtn} onPress={() => { haptic(); router.back(); }}>
            <Ionicons name="arrow-back" size={20} color="#FFF" />
          </TouchableOpacity>

          <View style={{ flex: 1 }}>
            <Text style={S.headerBrand}>Harmonia<Text style={S.headerDot}>.</Text></Text>
            <Text style={S.headerSub}>{t('notifications.header.subtitle')}</Text>
          </View>

          {unreadCount > 0 && (
            <View style={S.headerBadge}>
              <Text style={S.headerBadgeTxt}>{unreadCount}</Text>
            </View>
          )}
        </View>

        {/* Onglets */}
        <View style={S.tabRow}>
          {([
            { key: 'notifs',   icon: 'notifications-outline', label: t('notifications.tabs.notifs'),   count: unreadCount,    badgeColor: H.pink },
            { key: 'last24h',  icon: 'time-outline',           label: t('notifications.tabs.last24h'),  count: unread24hCount, badgeColor: H.gold },
            { key: 'trophees', icon: 'trophy-outline',         label: t('notifications.tabs.trophees'), count: trophies.length,badgeColor: H.gold },
          ] as const).map(tabItem => (
            <TouchableOpacity
              key={tabItem.key}
              style={[S.tabBtn, tab === tabItem.key && S.tabBtnActive]}
              onPress={() => handleTabChange(tabItem.key)}
              activeOpacity={0.8}
            >
              <Ionicons
                name={tabItem.icon as any}
                size={14}
                color={tab === tabItem.key ? H.gold : 'rgba(255,255,255,0.5)'}
              />
              <Text style={[S.tabLabel, tab === tabItem.key && S.tabLabelActive]}>
                {tabItem.label}
              </Text>
              {tabItem.count > 0 && (
                <View style={[S.tabPill, { backgroundColor: tabItem.badgeColor }]}>
                  <Text style={[S.tabPillTxt, tabItem.badgeColor === H.gold && { color: H.violetSoft }]}>{tabItem.count}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </LinearGradient>

      {/* ══ BARRE ERREUR ════════════════════════════════════════════════════ */}
      {!!error && (
        <View style={S.errBar}>
          <Ionicons name="alert-circle" size={14} color={H.red} />
          <Text style={S.errTxt} numberOfLines={1}>{error}</Text>
          <TouchableOpacity onPress={loadAll} style={S.errBtn}>
            <Text style={S.errBtnTxt}>{t('notifications.error.retry')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* ══ CONTENU ═════════════════════════════════════════════════════════ */}
      {loading ? (
        <View style={S.loadCenter}>
          <ActivityIndicator size="large" color={H.violet} />
          <Text style={S.loadTxt}>{t('notifications.loading.default')}</Text>
        </View>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingTop: 14, paddingBottom: 60 }}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={H.violet} />
          }
          showsVerticalScrollIndicator={false}
        >

          {/* ══ ONGLET : NOTIFICATIONS ══════════════════════════════════════ */}
          {tab === 'notifs' && (
            <>
              {unreadCount > 0 && (
                <TouchableOpacity style={S.markAllRow} onPress={markAllRead} activeOpacity={0.75}>
                  <View style={S.markAllIcon}>
                    <Ionicons name="checkmark-done" size={13} color={H.violet} />
                  </View>
                  <Text style={S.markAllTxt}>{t('notifications.markAll.action')}</Text>
                  <View style={S.markAllBadge}>
                    <Text style={S.markAllBadgeTxt}>{unreadCount}</Text>
                  </View>
                </TouchableOpacity>
              )}

              {notifs.length === 0
                ? <EmptyState icon="notifications-off-outline" title={t('notifications.empty.notifs.title')} sub={t('notifications.empty.notifs.sub')} />
                : notifs.map(n => (
                    <NotifCard
                      key={n.id}
                      n={n}
                      t={t}
                      lang={lang}
                      onPress={() => markRead(n.id, 'notifs')}
                    />
                  ))
              }

              {hasMore && (
                <TouchableOpacity style={S.moreBtn} onPress={loadMore} disabled={loadingMore} activeOpacity={0.8}>
                  {loadingMore
                    ? <ActivityIndicator size="small" color={H.violet} />
                    : <>
                        <Ionicons name="chevron-down" size={15} color={H.violet} />
                        <Text style={S.moreTxt}>{t('notifications.more.loadOlder')}</Text>
                      </>
                  }
                </TouchableOpacity>
              )}

              {notifs.length > 0 && !hasMore && <EndMarker t={t} />}
            </>
          )}

          {/* ══ ONGLET : INFO 24H ═══════════════════════════════════════════ */}
          {tab === 'last24h' && (
            <>
              {/* Bandeau info — dégradé violet → indigo, identique au header */}
              <LinearGradient colors={[H.violet, H.indigo]} style={S.info24hBanner}>
                <View style={S.info24hIconWrap}>
                  <Ionicons name="time" size={22} color={H.gold} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={S.info24hTitle}>{t('notifications.info24h.title')}</Text>
                  <Text style={S.info24hSub}>
                    {loading24h
                      ? t('notifications.loading.info24h')
                      : t('notifications.info24h.count', { count: notifs24h.length })
                    }
                  </Text>
                </View>
                {unread24hCount > 0 && (
                  <View style={S.info24hBadge}>
                    <Text style={S.info24hBadgeTxt}>{t('notifications.info24h.unread', { count: unread24hCount })}</Text>
                  </View>
                )}
              </LinearGradient>

              {loading24h ? (
                <View style={S.loadCenter}>
                  <ActivityIndicator size="large" color={H.violet} />
                  <Text style={S.loadTxt}>{t('notifications.loading.info24hFull')}</Text>
                </View>
              ) : notifs24h.length === 0 ? (
                <EmptyState
                  icon="time-outline"
                  title={t('notifications.empty.last24h.title')}
                  sub={t('notifications.empty.last24h.sub')}
                />
              ) : (
                notifs24h.map(n => (
                  <NotifCard
                    key={n.id}
                    n={n}
                    t={t}
                    lang={lang}
                    showCount
                    onPress={() => markRead(n.id, 'last24h')}
                  />
                ))
              )}

              {notifs24h.length > 0 && !loading24h && <EndMarker t={t} />}
            </>
          )}

          {/* ══ ONGLET : TROPHÉES ═══════════════════════════════════════════ */}
          {tab === 'trophees' && (
            <>
              {trophies.length === 0 ? (
                <EmptyState
                  emoji="🏆"
                  title={t('notifications.empty.trophees.title')}
                  sub={t('notifications.empty.trophees.sub')}
                />
              ) : (
                <>
                  {/* Bannière compteur — dégradé or → rose, cohérent avec le bouton créer de actu.tsx */}
                  <LinearGradient colors={[H.gold, H.pink]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={S.trophyBanner}>
                    <Text style={S.trophyBannerEmoji}>🏆</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={S.trophyBannerCount}>{trophies.length}</Text>
                      <Text style={S.trophyBannerLabel}>
                        {t('notifications.trophy.won', { count: trophies.length })}
                      </Text>
                    </View>
                    <Ionicons name="star" size={52} color="rgba(255,255,255,0.25)" />
                  </LinearGradient>

                  {trophies.map((tr, i) => (
                    <TrophyCard key={tr.id} t={tr} rank={trophies.length - i} translate={t} lang={lang} />
                  ))}
                </>
              )}
            </>
          )}

        </ScrollView>
      )}
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARTE NOTIFICATION
// ─────────────────────────────────────────────────────────────────────────────
function NotifCard({
  n, onPress, showCount = false, t, lang,
}: { n: Notif; onPress: () => void; showCount?: boolean; t: (key: string, opts?: any) => string; lang: string }) {
  const theme = notifTheme(n.type, t);
  const date  = n.last_activity_at || n.created_at;

  return (
    <TouchableOpacity
      style={[S.card, !n.is_read && S.cardUnread]}
      onPress={onPress}
      activeOpacity={0.82}
    >
      {/* Accent latéral non-lu */}
      {!n.is_read && <View style={S.cardAccent} />}

      {/* Icône */}
      <View style={[S.cardIconWrap, { backgroundColor: theme.bg }]}>
        <Ionicons name={theme.icon as any} size={20} color={theme.color} />
      </View>

      {/* Corps */}
      <View style={S.cardBody}>
        <View style={S.cardTopRow}>
          <View style={[S.typePill, { backgroundColor: theme.bg }]}>
            <Text style={[S.typePillTxt, { color: theme.color }]}>{theme.label}</Text>
          </View>
          <Text style={S.cardTime}>{fmtRelative(date, t, lang)}</Text>
        </View>

        <Text style={[S.cardTitle, !n.is_read && S.cardTitleUnread]} numberOfLines={1}>
          {n.title}
        </Text>
        <Text style={S.cardBody2} numberOfLines={3}>{n.body}</Text>

        {/* Compteur groupé (24h) */}
        {showCount && n.total_count > 1 && (
          <View style={S.countRow}>
            <Ionicons name="layers-outline" size={11} color={H.violet} />
            <Text style={S.countTxt}>{t('notifications.card.grouped', { count: n.total_count })}</Text>
          </View>
        )}
      </View>

      {/* Dot non-lu */}
      {!n.is_read && (
        <View style={S.dotWrap}>
          <View style={S.dot} />
        </View>
      )}
    </TouchableOpacity>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARTE TROPHÉE
// ─────────────────────────────────────────────────────────────────────────────
function TrophyCard({ t, rank, translate, lang }: { t: Trophy; rank: number; translate: (key: string, opts?: any) => string; lang: string }) {
  return (
    <View style={S.trophyCard}>
      <Text style={S.trophyRank}>#{rank}</Text>

      <View style={S.trophyMedal}>
        <Text style={{ fontSize: 22 }}>🏆</Text>
      </View>

      <View style={S.trophyBody}>
        <Text style={S.trophyTitle}>{t.title}</Text>
        {t.description ? <Text style={S.trophyDesc}>{t.description}</Text> : null}
        {t.reason ? (
          <View style={S.trophyReasonRow}>
            <Ionicons name="chatbubble-ellipses-outline" size={11} color="#B8860B" />
            <Text style={S.trophyReason}>{t.reason}</Text>
          </View>
        ) : null}
        {t.awarder && (
          <Text style={S.trophyAwarder}>
            {translate('notifications.trophy.awardedBy', { firstName: t.awarder.prenom, lastName: t.awarder.nom })}
          </Text>
        )}
        <Text style={S.trophyDate}>{fmtDate(t.awarded_at, lang)}</Text>
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────
function EmptyState({
  icon, emoji, title, sub,
}: { icon?: string; emoji?: string; title: string; sub: string }) {
  return (
    <View style={S.empty}>
      <View style={S.emptyOrbe}>
        {emoji
          ? <Text style={{ fontSize: 44 }}>{emoji}</Text>
          : <Ionicons name={icon as any} size={40} color={H.faint} />
        }
      </View>
      <Text style={S.emptyTitle}>{title}</Text>
      <Text style={S.emptySub}>{sub}</Text>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIN DE LISTE
// ─────────────────────────────────────────────────────────────────────────────
function EndMarker({ t }: { t: (key: string) => string }) {
  return (
    <View style={S.endRow}>
      <View style={S.endLine} />
      <Text style={S.endTxt}>{t('notifications.end.upToDate')}</Text>
      <View style={S.endLine} />
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  root: { flex: 1, backgroundColor: H.bg },

  // ── Header ──────────────────────────────────────────────────────────────
  header: {
    paddingTop: Platform.OS === 'ios' ? 56 : 38,
    paddingBottom: 0,
    paddingHorizontal: 18,
  },

  // Titre
  headerRow:      { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20 },
  backBtn:        { width: 38, height: 38, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  headerBrand:    { fontSize: 22, fontWeight: '900', color: '#FFFFFF', letterSpacing: -0.5 },
  headerDot:      { color: H.gold },
  headerSub:      { fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2, fontWeight: '600' },
  headerBadge:    { backgroundColor: H.pink, borderRadius: 12, minWidth: 24, height: 24, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 6 },
  headerBadgeTxt: { color: '#FFF', fontSize: 11, fontWeight: '900' },

  // Onglets
  tabRow:        { flexDirection: 'row', gap: 6, paddingBottom: 20 },
  tabBtn:        { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 10, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.12)' },
  tabBtnActive:  { backgroundColor: 'rgba(255,255,255,0.24)', borderWidth: 1, borderColor: `${H.gold}66` },
  tabLabel:      { fontSize: 11, fontWeight: '700', color: 'rgba(255,255,255,0.55)' },
  tabLabelActive:{ color: '#FFFFFF' },
  tabPill:       { borderRadius: 8, minWidth: 17, height: 17, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  tabPillTxt:    { color: '#FFF', fontSize: 9, fontWeight: '900' },

  // ── Erreur ───────────────────────────────────────────────────────────────
  errBar:    { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 16, marginTop: 10, backgroundColor: '#FEF2F2', borderRadius: 12, padding: 12, borderLeftWidth: 3, borderLeftColor: H.red },
  errTxt:    { flex: 1, fontSize: 12, color: H.red },
  errBtn:    { paddingHorizontal: 10, paddingVertical: 4, backgroundColor: H.red, borderRadius: 8 },
  errBtnTxt: { color: '#FFF', fontSize: 11, fontWeight: '700' },

  // ── Loading ───────────────────────────────────────────────────────────────
  loadCenter: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 16, paddingTop: 60 },
  loadTxt:    { fontSize: 13, color: H.muted, fontWeight: '600' },

  // ── Mark all ──────────────────────────────────────────────────────────────
  markAllRow:      { flexDirection: 'row', alignItems: 'center', gap: 10, marginHorizontal: 16, marginBottom: 12, backgroundColor: H.violetPale, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 11, borderWidth: 1, borderColor: H.violetLine },
  markAllIcon:     { width: 26, height: 26, borderRadius: 8, backgroundColor: '#E0D0FF', justifyContent: 'center', alignItems: 'center' },
  markAllTxt:      { flex: 1, fontSize: 13, color: H.violet, fontWeight: '700' },
  markAllBadge:    { backgroundColor: H.pink, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 },
  markAllBadgeTxt: { color: '#FFF', fontSize: 11, fontWeight: '800' },

  // ── Carte notification ────────────────────────────────────────────────────
  card: {
    flexDirection: 'row', alignItems: 'flex-start',
    backgroundColor: H.surface,
    marginHorizontal: 16, marginBottom: 10,
    borderRadius: 18, padding: 14, gap: 12,
    borderWidth: 1, borderColor: H.border,
    overflow: 'hidden',
  },
  cardUnread: {
    backgroundColor: '#FAF7FF',
    borderColor: H.violetLine,
  },
  cardAccent:    { position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: H.gold },
  cardIconWrap:  { width: 44, height: 44, borderRadius: 14, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  cardBody:      { flex: 1 },
  cardTopRow:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 },
  typePill:      { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 },
  typePillTxt:   { fontSize: 10, fontWeight: '800', letterSpacing: 0.2 },
  cardTime:      { fontSize: 11, color: H.muted, fontWeight: '600' },
  cardTitle:     { fontSize: 14, fontWeight: '700', color: H.soft, marginBottom: 3, lineHeight: 19 },
  cardTitleUnread: { color: H.text, fontWeight: '800' },
  cardBody2:     { fontSize: 13, color: H.muted, lineHeight: 19 },
  countRow:      { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 6 },
  countTxt:      { fontSize: 11, color: H.violet, fontWeight: '600' },
  dotWrap:       { paddingTop: 4, flexShrink: 0 },
  dot:           { width: 8, height: 8, borderRadius: 4, backgroundColor: H.gold },

  // ── Bandeau 24h ───────────────────────────────────────────────────────────
  info24hBanner:   { flexDirection: 'row', alignItems: 'center', gap: 12, marginHorizontal: 16, marginBottom: 14, borderRadius: 18, padding: 16 },
  info24hIconWrap: { width: 42, height: 42, borderRadius: 13, backgroundColor: 'rgba(255,255,255,0.16)', justifyContent: 'center', alignItems: 'center' },
  info24hTitle:    { fontSize: 15, fontWeight: '800', color: '#FFF' },
  info24hSub:      { fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2, fontWeight: '600' },
  info24hBadge:    { backgroundColor: H.gold, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4 },
  info24hBadgeTxt: { color: H.violetSoft, fontSize: 11, fontWeight: '900' },

  // ── Voir plus / fin ───────────────────────────────────────────────────────
  moreBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginHorizontal: 16, marginTop: 4, marginBottom: 8, paddingVertical: 14, backgroundColor: H.violetPale, borderRadius: 16, borderWidth: 1, borderColor: H.violetLine },
  moreTxt: { fontSize: 13, color: H.violet, fontWeight: '700' },
  endRow:  { flexDirection: 'row', alignItems: 'center', gap: 12, marginHorizontal: 20, marginVertical: 24 },
  endLine: { flex: 1, height: 1, backgroundColor: H.violetLine },
  endTxt:  { fontSize: 12, color: H.violetMuted, fontWeight: '600' },

  // ── Trophée bannière ──────────────────────────────────────────────────────
  trophyBanner:      { flexDirection: 'row', alignItems: 'center', gap: 14, marginHorizontal: 16, marginBottom: 14, borderRadius: 20, padding: 20 },
  trophyBannerEmoji: { fontSize: 42 },
  trophyBannerCount: { fontSize: 36, fontWeight: '900', color: '#FFF', lineHeight: 40 },
  trophyBannerLabel: { fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: '700' },

  // ── Trophée carte ─────────────────────────────────────────────────────────
  trophyCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    backgroundColor: H.goldBg,
    marginHorizontal: 16, marginBottom: 10,
    borderRadius: 18, padding: 16,
    borderWidth: 1, borderColor: '#FDE68A',
  },
  trophyRank:      { position: 'absolute', top: 10, right: 14, fontSize: 10, color: '#B8860B', fontWeight: '900' },
  trophyMedal:     { width: 50, height: 50, borderRadius: 15, justifyContent: 'center', alignItems: 'center', flexShrink: 0, backgroundColor: H.gold },
  trophyBody:      { flex: 1 },
  trophyTitle:     { fontSize: 15, fontWeight: '800', color: '#92400E', marginBottom: 3 },
  trophyDesc:      { fontSize: 13, color: '#78350F', lineHeight: 18, marginBottom: 4 },
  trophyReasonRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 4 },
  trophyReason:    { fontSize: 12, color: '#B8860B', fontStyle: 'italic', flex: 1 },
  trophyAwarder:   { fontSize: 11, color: '#B8860B', fontWeight: '600', marginBottom: 3 },
  trophyDate:      { fontSize: 11, color: '#B45309', fontWeight: '600' },

  // ── Empty ─────────────────────────────────────────────────────────────────
  emptyOrbe:  { width: 90, height: 90, borderRadius: 45, backgroundColor: H.violetPale, borderWidth: 2, borderColor: H.violetLine, justifyContent: 'center', alignItems: 'center', marginBottom: 6 },
  empty:      { alignItems: 'center', paddingTop: 70, paddingBottom: 40, gap: 10 },
  emptyTitle: { fontSize: 18, fontWeight: '800', color: H.soft },
  emptySub:   { fontSize: 13, color: H.muted, textAlign: 'center', maxWidth: 260, lineHeight: 20 },
});
